import React, { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

/**
 * Un número que cuenta hasta su valor cuando entra en pantalla.
 *
 * Arranca recién al verse y una sola vez: si contara al montar, en una landing
 * el número ya habría terminado de subir mucho antes de que llegues a
 * scrollear hasta él, y no se vería nunca la animación.
 *
 * `decimales` existe porque no todos los datos son enteros. `sufijo` y
 * `prefijo` van acá adentro y no al lado del componente para que el "%" no se
 * separe del número al cambiar de renglón.
 *
 * Si el sistema pide menos movimiento, muestra el valor final sin animar: el
 * dato es el mismo, lo que se saca es el efecto.
 */
export default function ContadorAnimado({
  hasta,
  desde = 0,
  duracion = 1600,
  decimales = 0,
  prefijo = '',
  sufijo = '',
  className,
}) {
  const referencia = useRef(null);
  const enPantalla = useInView(referencia, { once: true, amount: 0.5 });
  const menosMovimiento = useReducedMotion();
  const [valor, setValor] = useState(desde);

  useEffect(() => {
    if (!enPantalla) return;
    if (menosMovimiento) {
      setValor(hasta);
      return;
    }

    let cuadro;
    let inicio = null;
    // Desaceleración al final: sube rápido y frena, que es como se leen bien
    // los números. Lineal parece un cronómetro.
    const suavizar = (t) => 1 - Math.pow(1 - t, 3);

    const paso = (ahora) => {
      if (inicio === null) inicio = ahora;
      const avance = Math.min((ahora - inicio) / duracion, 1);
      setValor(desde + (hasta - desde) * suavizar(avance));
      if (avance < 1) cuadro = requestAnimationFrame(paso);
    };

    cuadro = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(cuadro);
  }, [enPantalla, menosMovimiento, hasta, desde, duracion]);

  // El valor final se escribe siempre en el DOM para quien lea con lector de
  // pantalla o llegue con el buscador: lo que se anima es solo lo que se ve.
  return (
    <span ref={referencia} className={className}>
      <span aria-hidden="true">
        {prefijo}
        {valor.toFixed(decimales)}
        {sufijo}
      </span>
      <span className="sr-only">
        {prefijo}
        {hasta.toFixed(decimales)}
        {sufijo}
      </span>
    </span>
  );
}
